import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BadgeCheck, CircleCheckBig } from 'lucide-react';
import { authAwareRefetchInterval } from '@siliconx/shared/query/queryClient';
import { cn } from '@siliconx/shared/ui/utils';
import type { DeviceResult, ReviewReason } from '@siliconx/shared/inventory/hardware-ingest-schema';
import { api, throwIfNotOk } from '../lib/api';
import { qk } from '../lib/queryKeys';

/**
 * Probe asset grouping (spec section 5): after a token is minted, list each
 * machine as its reading arrives, so the person running the script sees the
 * result without leaving the page. Filtered server-side by token id: only
 * one token per org is active, so every row with this id came after the mint.
 *
 * A foreign-org device never appears as a per-device result - the vocabulary
 * has no `foreign_org` value (spec D11: a per-device report would make this
 * an enumeration oracle against the exchange-wide identity index). The count
 * lives in `foreignOrgDeviceCount`, and it is withheld under a device-count
 * floor server-side, so it can legitimately be null even on a real reading.
 */

/** Mirrors GET /api/client/inventory/ingests?tokenId= (Plan 4, gap G1). */
export interface ProbeIngestRow {
  id: string;
  status: string;
  resolvedAssetId: string | null;
  message: string | null;
  createdAt: string;
  inventoryTokenId: string | null;
  productName: string | null;
  sysVendor: string | null;
  deviceCount: number;
  deviceResults: { deviceIndex: number; result: DeviceResult }[] | null;
  foreignOrgDeviceCount: number | null;
  /** SIL-1188/SIL-1191. Set only when status stays `imported_customs` for the
   * SIL-1188 confidence check's own reason - see `REVIEW_REASON_LABEL` below. */
  reviewReason?: ReviewReason | null;
}

/**
 * SIL-1191. Plain-language reasons a Customs row shows next to "Needs
 * review" - mirrors `reviewReasonSchema`'s doc comment in
 * `hardware-ingest-schema.ts`. A `imported_customs` row with no
 * `reviewReason` (a pre-SIL-1188 cause: a conflict-adjacent import, a
 * multi-device bare-accelerator reading, etc.) falls back to
 * `DEFAULT_REVIEW_REASON_LABEL`.
 */
export const REVIEW_REASON_LABEL: Record<ReviewReason, string> = {
  no_candidate: 'no catalog match found',
  tied_candidate: 'multiple catalog matches tied',
  low_overlap: "closest catalog match wasn't a strong enough fit",
  unmatched_child: 'not every component matched',
};
export const DEFAULT_REVIEW_REASON_LABEL = 'needs manual review';

export const PROBE_INGEST_POLL_MS = 5_000;

/** SIL-1195: how long the newest reading keeps the accent treatment before going neutral. */
export const PROBE_ROW_EMPHASIS_MS = 4_000;

/**
 * SIL-1195/SIL-1206: totals shown on the "Done capturing" session summary
 * (ProbeSessionSummary). `total` is every reading the probe reported this
 * session, regardless of outcome, and now ALWAYS equals the sum of the four
 * named buckets below - every `IngestOutcome` status
 * (apps/api/src/services/inventory/ingest.ts) lands in exactly one:
 *
 *   - `addedToAssets`   - enriched_existing / attached_provided /
 *                         imported_matched / imported_active (SIL-1216: an
 *                         individual-org capture, matched or not): landed
 *                         straight on an asset.
 *   - `inCustoms`       - imported_customs (manual triage) AND
 *                         collision_pending (SIL-1206: promotion out of
 *                         Customs requires zero collisions, so this status
 *                         stays quarantined even though `resolvedAssetId` is
 *                         set - it must NOT count as added).
 *   - `alreadyInAssets` - every `conflict_*` status: the reading collided
 *                         with hardware identity this org already owns.
 *   - `rejected`        - every `rejected_*` status: never wrote anything.
 */
export interface ProbeSessionTotals {
  total: number;
  addedToAssets: number;
  inCustoms: number;
  alreadyInAssets: number;
  rejected: number;
}
export function computeProbeSessionTotals(rows: ProbeIngestRow[]): ProbeSessionTotals {
  let addedToAssets = 0;
  let inCustoms = 0;
  let alreadyInAssets = 0;
  let rejected = 0;
  for (const row of rows) {
    if (row.status.startsWith('conflict')) alreadyInAssets++;
    else if (row.status.startsWith('rejected')) rejected++;
    else if (needsCustomsTriage(row)) inCustoms++;
    else if (isRowMatched(row)) addedToAssets++;
  }
  return { total: rows.length, addedToAssets, inCustoms, alreadyInAssets, rejected };
}

function summarizeDevices(row: ProbeIngestRow) {
  const results = row.deviceResults ?? [];
  const attached = results.filter(
    (d) => d.result === 'created' || d.result === 'updated' || d.result === 'moved',
  ).length;
  return {
    // A reading without chassis data has no per-device results (spec section 2, rule 4).
    gpuCount: row.deviceResults ? attached : row.deviceCount,
    held: results.filter((d) => d.result === 'collision' || d.result === 'blocked').length,
  };
}

/**
 * SIL-1183: what the Add Asset success panel names as "what was captured"
 * for a matched TrueRead reading, e.g. "Supermicro SYS-821GE · 8 GPUs" - the
 * probe never reports a serial, so this is deliberately name + count, not a
 * serial-style label. `Partial<ProbeIngestRow>` (rather than the full type)
 * because a test double for `onMatchesChange` only needs to carry the
 * fields this actually reads.
 */
export function probeCaptureSummary(row: Partial<ProbeIngestRow>): { name: string; gpuCount: number } {
  const results = row.deviceResults ?? [];
  const attached = results.filter(
    (d) => d.result === 'created' || d.result === 'updated' || d.result === 'moved',
  ).length;
  const gpuCount = row.deviceResults ? attached : (row.deviceCount ?? 0);
  const name = [row.sysVendor, row.productName].filter(Boolean).join(' ') || 'Unidentified hardware';
  return { name, gpuCount };
}

/**
 * `imported_customs` and `collision_pending` are the two statuses that mean
 * "landed in Customs, needs manual triage" - `enriched_existing`/
 * `attached_provided` matched the reading straight to an asset,
 * `imported_matched` (SIL-1188: a first-time capture confidently SKU-matched
 * at ingest) is created outside Customs entirely, and `imported_active`
 * (SIL-1216: an individual-org capture, matched or not - no confidence gate
 * for that org type) likewise never touches Customs (see
 * apps/api/src/services/inventory/ingest.ts's IngestOutcome status union).
 * `resolvedAssetId` is populated for ALL of these (including
 * `imported_customs`/`collision_pending`, which point at the new
 * Customs-quarantined asset), so it alone can't distinguish "added" from
 * "needs triage" - the status has to gate which link/copy shows.
 *
 * SIL-1206: `collision_pending` is added here deliberately. Promotion out of
 * Customs requires zero collisions (ingest.ts step 8, `finalStatus =
 * collisionIds.length > 0 ? 'collision_pending' : status`), so a
 * collision_pending asset stays quarantined even though a resolvedAssetId
 * exists - counting it as "matched"/"Added to Assets" (the pre-SIL-1206
 * behavior) was the root cause of totals that didn't add up.
 */
function needsCustomsTriage(row: ProbeIngestRow) {
  return row.status === 'imported_customs' || row.status === 'collision_pending';
}

/**
 * Exported for `ProbeImportPanel` (submit.tsx): feeds the "remember this
 * method" side effect and (SIL-1195) `computeProbeSessionTotals`'s
 * `addedToAssets` count on the "Done capturing" session summary.
 */
export function isRowMatched(row: ProbeIngestRow) {
  const failed = row.status.startsWith('conflict') || row.status.startsWith('rejected');
  return !failed && !needsCustomsTriage(row) && !!row.resolvedAssetId;
}

function ProbeIngestItem({ row, emphasized }: { row: ProbeIngestRow; emphasized: boolean }) {
  const { gpuCount, held } = summarizeDevices(row);
  const name = [row.sysVendor, row.productName].filter(Boolean).join(' ') || 'Unidentified hardware';
  const failed = row.status.startsWith('conflict') || row.status.startsWith('rejected');
  const foreign = row.foreignOrgDeviceCount ?? 0;
  const triage = needsCustomsTriage(row);
  const matched = isRowMatched(row);

  return (
    <li
      data-emphasized={emphasized}
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 p-3 text-sm transition-colors duration-500',
        // SIL-1195: arrival animation - CSS `animation` only plays on
        // mount, never on a re-render of an already-mounted `<li>` with the
        // same `key`, so a poll refetch that returns the same rows (just a
        // new array reference) never replays this. The global
        // prefers-reduced-motion floor (globals.css) already zeroes every
        // animation-duration, so no separate JS branch is needed for that.
        'animate-[probe-row-slide-fade-in_300ms_ease-out] motion-reduce:animate-none',
        // SIL-1195: newest-row emphasis - the caller guarantees at most one
        // row is ever `emphasized` at a time.
        emphasized && 'bg-brand-50/60 ring-1 ring-inset ring-brand-200',
      )}
    >
      <div className="min-w-0">
        {matched ? (
          <div className="flex items-center gap-1.5">
            <CircleCheckBig aria-hidden="true" className="h-4 w-4 flex-none text-emerald-600" />
            <p className="font-medium text-foreground">Captured — {name}</p>
          </div>
        ) : (
          <p className="font-medium text-foreground">{name}</p>
        )}
        <p className="text-xs text-muted-foreground">
          {gpuCount} {gpuCount === 1 ? 'GPU' : 'GPUs'} - {new Date(row.createdAt).toLocaleTimeString()}
        </p>
        {matched && (
          <span className="mt-1 inline-flex items-center gap-1 rounded-md bg-brand-50 px-1.5 py-0.5 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-200">
            <BadgeCheck className="h-3 w-3" /> Verified by TrueRead
          </span>
        )}
        {foreign > 0 && (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            {foreign} {foreign === 1 ? 'GPU is' : 'GPUs are'} registered to another organization
          </p>
        )}
        {held > 0 && (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            {held} {held === 1 ? 'GPU is' : 'GPUs are'} held for a serial review
          </p>
        )}
        {failed && row.message && <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{row.message}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        {matched && (
          <>
            <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">Added to Assets</span>
            {/* SIL-1191/SIL-1195: neutral, not brand-accented - a row can be
                one of several that matched in the same reading, and the
                view's one accent focal point is either this row's own
                ~4s newest-reading emphasis or the step-level session
                summary (ProbeSessionSummary), never a per-row link. */}
            <Link
              to="/assets/$assetId"
              params={{ assetId: row.resolvedAssetId as string }}
              className="text-sm font-medium text-foreground hover:underline"
            >
              View asset
            </Link>
          </>
        )}
        {triage && (
          <>
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
              Needs review - {row.reviewReason ? REVIEW_REASON_LABEL[row.reviewReason] : DEFAULT_REVIEW_REASON_LABEL}
            </span>
            <Link to="/customs" className="text-sm font-medium text-foreground hover:underline">
              Triage in Customs
            </Link>
          </>
        )}
      </div>
    </li>
  );
}

/** Plain-language, single-line summary of one row - drives the SIL-1195 targeted arrival announcement. */
function describeRowForAnnouncement(row: ProbeIngestRow): string {
  if (isRowMatched(row)) return `New reading: Captured — ${probeCaptureSummary(row).name}.`;
  if (needsCustomsTriage(row)) return 'New reading: needs review.';
  if (row.status.startsWith('conflict') || row.status.startsWith('rejected')) {
    return `New reading: ${row.message ?? 'could not be captured'}.`;
  }
  return 'New reading received.';
}

export interface ProbeIngestFeedProps {
  tokenId: string;
  /** Fires whenever the set of rows changes, with the full list of matched rows (added an asset, not Customs triage) - not just a yes/no. */
  onMatchesChange?: (matches: ProbeIngestRow[]) => void;
  /** SIL-1195: fires whenever the row set changes, with every row (any status) - feeds `computeProbeSessionTotals` for the "Done capturing" summary. */
  onRowsChange?: (rows: ProbeIngestRow[]) => void;
}

export function ProbeIngestFeed({ tokenId, onMatchesChange, onRowsChange }: ProbeIngestFeedProps) {
  const { data, isError } = useQuery({
    queryKey: qk.inventory.ingestsForToken(tokenId),
    queryFn: async () => {
      const res = await api.api.client.inventory.ingests.$get({ query: { tokenId } });
      await throwIfNotOk(res);
      return (await res.json()).ingests as unknown as ProbeIngestRow[];
    },
    refetchInterval: authAwareRefetchInterval(PROBE_INGEST_POLL_MS),
  });
  const rows = data ?? [];
  const rowsKey = rows.map((r) => `${r.id}:${r.status}`).join(',');

  useEffect(() => {
    onMatchesChange?.(rows.filter(isRowMatched));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on row-content changes, not identity
  }, [rowsKey, onMatchesChange]);

  useEffect(() => {
    onRowsChange?.(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on row-content changes, not identity
  }, [rowsKey, onRowsChange]);

  // SIL-1195: newest-row emphasis - exactly one row (the latest arrival)
  // carries the accent treatment, for PROBE_ROW_EMPHASIS_MS, then goes
  // neutral. Keyed off the row's own id (not array/object identity), so a
  // same-content poll refetch never restarts the timer.
  const latestId = rows.length > 0 ? (rows[rows.length - 1]?.id ?? null) : null;
  const [emphasizedId, setEmphasizedId] = useState<string | null>(null);
  const lastEmphasizedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!latestId || latestId === lastEmphasizedForRef.current) return;
    lastEmphasizedForRef.current = latestId;
    setEmphasizedId(latestId);
    const t = setTimeout(() => setEmphasizedId(null), PROBE_ROW_EMPHASIS_MS);
    return () => clearTimeout(t);
  }, [latestId]);

  // SIL-1195: one targeted, polite announcement per arrival - not the whole
  // feed re-read on every poll. Keyed the same way as the emphasis timer.
  const [announcement, setAnnouncement] = useState('');
  const lastAnnouncedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!latestId || latestId === lastAnnouncedForRef.current) return;
    lastAnnouncedForRef.current = latestId;
    const latest = rows[rows.length - 1];
    if (latest) setAnnouncement(describeRowForAnnouncement(latest));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per new latestId; rows is read for its current value, not tracked as a dep
  }, [latestId]);

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Readings from this probe ({rows.length})
      </p>
      {rows.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-dashed border-border p-3">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-border border-t-brand-600" />
          Waiting for the first reading. Each machine appears here as TrueRead reports it.
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {rows.map((r) => (
            <ProbeIngestItem key={r.id} row={r} emphasized={r.id === emphasizedId} />
          ))}
        </ul>
      )}
      {isError && <p className="text-xs text-destructive">Could not check for new readings. Retrying.</p>}
      {/* SIL-1195: the one live region in this component - a short,
          targeted sentence per arrival, not the whole feed (which used to
          sit on the outer wrapper and re-announce everything on every
          poll). */}
      <p aria-live="polite" className="sr-only">{announcement}</p>
    </div>
  );
}
